import { PenLine, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import AppButton from "../../../components/ui/AppButton";
import AppInputField from "../../../components/ui/AppInputField";
import { Modal } from "../../../components/ui/Modal";
import SuccessView from "../../../components/ui/SuccessView";
import { useToast } from "../../../hooks/useToast";
import { removeAfterDash } from "../../../lib/utils";
import CrewDropdownMenu from "../../driver/ui/CrewDropDownMenu";
import DriverDropdownMenu from "../../driver/ui/DriverDropdownMenu";
import { Crew, Dispatch } from "../@types";
import { useUpdateDispatchMutation } from "../dispatchApiSlice";

interface UpdateCrewMemberProps {
  dispatch: Dispatch;
  refresh: () => void;
}

const UpdateCrewMember: React.FC<UpdateCrewMemberProps> = ({
  dispatch,
  refresh,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [driverId, setDriverId] = useState<string>(dispatch.driverId || "");
  const [crewMembers, setCrewMembers] = useState<Crew[]>(dispatch.crews || []);
  const [showSuccess, setShowSuccess] = useState(false);
  const toast = useToast();

  const [updateDispatch, { isLoading, isSuccess, reset }] =
    useUpdateDispatchMutation();

  // Update driver
  const handleUpdateDriver = useCallback((value: string) => {
    setDriverId(value);
  }, []);

  // Update crew member
  const handleUpdateCrewMember = useCallback(
    (index: number, field: "crewType" | "name", value: string) => {
      setCrewMembers((prev) =>
        prev.map((crew, i) =>
          i === index ? { ...crew, [field]: value } : crew,
        ),
      );
    },
    [],
  );

  const handleUpdateCrewMemberMultiple = useCallback(
    (index: number, updates: Partial<Crew>) => {
      setCrewMembers((prev) =>
        prev.map((crew, i) => (i === index ? { ...crew, ...updates } : crew)),
      );
    },
    [],
  );

  // Add new crew member
  const handleAddCrewMember = useCallback(() => {
    const newCrew: Crew = {
      id: `crew-${Date.now()}`,
      crewType: "",
      name: "",
    };
    setCrewMembers((prev) => [...prev, newCrew]);
  }, []);

  // Remove crew member
  const handleRemoveCrewMember = useCallback((index: number) => {
    setCrewMembers((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Validate form
  const validateForm = useCallback((): string | null => {
    if (!driverId) {
      return "Driver is required";
    }

    for (const crew of crewMembers) {
      if (!crew.crewType.trim()) {
        return "All crew types are required";
      }
      if (!crew.name) {
        return "All crew members must be selected";
      }
    }

    return null;
  }, [driverId, crewMembers]);

  // Submit handler
  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      toast.error({
        title: "Validation Error",
        message: validationError,
      });
      return;
    }

    try {
      await updateDispatch({
        id: dispatch.id,
        isCompleted: dispatch.isCompleted,
        incomes: dispatch.incomes,
        expenses: dispatch.expenses,
        vehicleId: dispatch.vehicleId,
        fuelExpense: dispatch.fuelExpense,
        fromLocation: dispatch.fromLocation,
        toLocation: dispatch.toLocation,
        approximateDistance: dispatch.approximateDistance,
        crews: crewMembers,
        totalExpectedIncome: dispatch.totalExpectedIncome,
        totalIncomeEarned: dispatch.totalIncomeEarned,
        totalPassengers: dispatch.totalPassengers,
        perPassengerRate: dispatch.perPassengerRate,
      }).unwrap();

      toast.success({
        title: "Success",
        message: "Driver and crew members updated successfully",
      });
      setShowSuccess(true);
    } catch (error) {
      const errorMessage =
        getErrorDetails(error).message ||
        "Failed to update driver and crew. Please try again.";

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  };

  // Close modal handler
  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setDriverId(dispatch.driverId || "");
    setCrewMembers(dispatch.crews || []);
    reset();
  }, [dispatch.driverId, dispatch.crews, reset]);

  // Close success handler
  const handleCloseSuccess = useCallback(() => {
    setShowSuccess(false);
    setOpenModal(false);
    reset();
    refresh();
  }, [reset, refresh]);

  // Render success view
  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseSuccess}
          heading="Success"
          message="Driver and crew members have been updated successfully."
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess ? "" : "Update Driver & Crew";

  return (
    <>
      <div
        onClick={() => setOpenModal(true)}
        className="flex items-end justify-end mt-8 cursor-pointer"
      >
        <div className="flex items-center gap-1 border border-secondary rounded-2xl px-2 py-1 text-[13px] text-secondary hover:bg-secondary/5 transition-colors">
          Edit <PenLine className="h-4 w-4" />
        </div>
      </div>

      <Modal
        isOpen={openModal}
        onClose={handleCloseModal}
        title={modalTitle}
        className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white px-2 pb-4 overflow-hidden"
      >
        {isSuccess ? (
          renderSuccessView()
        ) : (
          <div className="p-6 space-y-4">
            <div className="flex flex-col gap-8 border border-gray-200 rounded-[10px] p-4">
              {/* Driver Section - Not Deletable, Only Editable */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Driver</h3>
                <DriverDropdownMenu
                  value={driverId}
                  onChange={handleUpdateDriver}
                  showLabel={false}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                  placeholder="Select driver"
                />
              </div>

              {/* Crew Section - Can be Added and Deleted */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold">
                    Crew Members{" "}
                    <span className="text-gray-500 font-normal">
                      (Optional)
                    </span>
                  </h3>
                </div>

                {crewMembers.length > 0 ? (
                  crewMembers.map((crew, index) => (
                    <div key={crew.id || index} className="space-y-2">
                      <div className="flex gap-4 items-center">
                        <AppInputField
                          type="text"
                          value={crew.crewType}
                          onChange={(e) =>
                            handleUpdateCrewMember(
                              index,
                              "crewType",
                              e.target.value,
                            )
                          }
                          className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-[15rem] mt-2"
                          placeholder="Enter crew type"
                        />
                        <CrewDropdownMenu
                          value={crew.id}
                          onChange={(value) =>
                            handleUpdateCrewMemberMultiple(index, {
                              id: value?.id || "",
                              name: removeAfterDash(value?.name) || "",
                            })
                          }
                          showLabel={false}
                          className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                          placeholder="Select crew member"
                        />
                        <div className="w-[90px] bg-red-100 flex items-center justify-center h-[48px] rounded-[10px]">
                          <button
                            type="button"
                            onClick={() => handleRemoveCrewMember(index)}
                            className="text-red-500 hover:text-red-600 transition-colors p-1"
                            aria-label="Remove crew member"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 py-2">
                    No crew members added yet
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleAddCrewMember}
                  className="text-secondary hover:text-primary transition-colors text-[13px] font-semibold"
                >
                  + Add more
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-start items-center gap-4 pt-4">
              <AppButton
                type="button"
                onClick={handleCloseModal}
                disabled={isLoading}
                className="px-6 py-2 bg-[#F6F6F6] rounded-md text-gray-700 hover:bg-gray-200 transition-colors text-[15px]"
              >
                Cancel
              </AppButton>

              <AppButton
                type="button"
                onClick={handleSubmit}
                disabled={isLoading}
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-[15px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Updating..." : "Update"}
              </AppButton>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default UpdateCrewMember;
